import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CreateReceiptPresetApplicationErrors,
  DeleteReceiptPresetApplicationErrors,
  GetDefaultReceiptPresetApplicationErrors,
  GetDefaultReceiptPresetResponse,
  ListReceiptPresetsApplicationErrors,
  ListReceiptPresetsResponse,
  ReceiptPreset,
  ReceiptPresetCreatedEnvelope,
  ReceiptPresetDeletedEnvelope,
  ReceiptPresetUpdatedEnvelope,
  SetDefaultReceiptPresetResponse,
  UpdateExtendedFieldsOptions,
  UpdateExtendedFieldsResponse,
  UpdateReceiptPreset,
  UpdateReceiptPresetApplicationErrors,
  createReceiptPreset as universalCreateReceiptPreset,
  deleteReceiptPreset as universalDeleteReceiptPreset,
  getDefaultReceiptPreset as universalGetDefaultReceiptPreset,
  getReceiptPreset as universalGetReceiptPreset,
  listReceiptPresets as universalListReceiptPresets,
  setDefaultReceiptPreset as universalSetDefaultReceiptPreset,
  updateExtendedFields as universalUpdateExtendedFields,
  updateReceiptPreset as universalUpdateReceiptPreset,
} from './receipts-presets-v1-receipt-preset-receipt-presets.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/get-paid' };

export function createReceiptPreset(
  httpClient: HttpClient
): CreateReceiptPresetSignature {
  return (receiptPreset: ReceiptPreset) =>
    universalCreateReceiptPreset(
      receiptPreset,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateReceiptPresetSignature {
  /**
   * Creates a receipt preset.
   * @param - Receipt preset to create.
   * @returns Created receipt preset.
   */
  (receiptPreset: ReceiptPreset): Promise<
    NonNullablePaths<
      ReceiptPreset,
      | `name`
      | `displaySettings.businessDisplayOptions.showBusinessEmail`
      | `displaySettings.businessDisplayOptions.showBusinessPhoneNumber`
      | `displaySettings.businessDisplayOptions.showBusinessAddress`
      | `displaySettings.customerDisplayOptions.showCustomerEmail`
      | `displaySettings.customerDisplayOptions.showCustomerPhoneNumber`
      | `displaySettings.customerDisplayOptions.showCustomerShippingAddress`
      | `displaySettings.customerDisplayOptions.showCustomerBillingAddress`
      | `displaySettings.customerDisplayOptions.showCustomerVatId`
      | `displaySettings.customerDisplayOptions.showCustomerCompanyName`
      | `displaySettings.itemsDisplayOptions.showItemsDescription`
      | `displaySettings.itemsDisplayOptions.showTax`
      | `displaySettings.totalsDisplayOptions.showTaxBreakdown`
      | `displaySettings.totalsDisplayOptions.showItemSubtotalPerTaxBreakdown`
      | `customFields.headerCustomFields`
      | `customFields.headerCustomFields.${number}.value`
      | `customFields.businessCustomFields`
      | `customFields.customerCustomFields`
      | `customFields.footerCustomFields`
      | `customFields.footerCustomFields.${number}.title`
      | `default`,
      5
    > & {
      __applicationErrorsType?: CreateReceiptPresetApplicationErrors;
    }
  >;
}

export function getReceiptPreset(
  httpClient: HttpClient
): GetReceiptPresetSignature {
  return (receiptPresetId: string) =>
    universalGetReceiptPreset(
      receiptPresetId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetReceiptPresetSignature {
  /**
   * Retrieves a receipt preset.
   * @param - Receipt preset ID to retrieve.
   * @returns Requested receipt preset.
   */
  (receiptPresetId: string): Promise<
    NonNullablePaths<
      ReceiptPreset,
      | `name`
      | `displaySettings.businessDisplayOptions.showBusinessEmail`
      | `displaySettings.businessDisplayOptions.showBusinessPhoneNumber`
      | `displaySettings.businessDisplayOptions.showBusinessAddress`
      | `displaySettings.customerDisplayOptions.showCustomerEmail`
      | `displaySettings.customerDisplayOptions.showCustomerPhoneNumber`
      | `displaySettings.customerDisplayOptions.showCustomerShippingAddress`
      | `displaySettings.customerDisplayOptions.showCustomerBillingAddress`
      | `displaySettings.customerDisplayOptions.showCustomerVatId`
      | `displaySettings.customerDisplayOptions.showCustomerCompanyName`
      | `displaySettings.itemsDisplayOptions.showItemsDescription`
      | `displaySettings.itemsDisplayOptions.showTax`
      | `displaySettings.totalsDisplayOptions.showTaxBreakdown`
      | `displaySettings.totalsDisplayOptions.showItemSubtotalPerTaxBreakdown`
      | `customFields.headerCustomFields`
      | `customFields.headerCustomFields.${number}.value`
      | `customFields.businessCustomFields`
      | `customFields.customerCustomFields`
      | `customFields.footerCustomFields`
      | `customFields.footerCustomFields.${number}.title`
      | `default`,
      5
    >
  >;
}

export function getDefaultReceiptPreset(
  httpClient: HttpClient
): GetDefaultReceiptPresetSignature {
  return () =>
    universalGetDefaultReceiptPreset(
      // @ts-ignore
      { httpClient }
    );
}

interface GetDefaultReceiptPresetSignature {
  /**
   * Retrieves the default receipt preset.
   *
   * The default preset is used during receipt creation when a preset isn't specified or doesn't exist.
   */
  (): Promise<
    NonNullablePaths<
      GetDefaultReceiptPresetResponse,
      | `receiptPreset.name`
      | `receiptPreset.displaySettings.businessDisplayOptions.showBusinessEmail`
      | `receiptPreset.displaySettings.businessDisplayOptions.showBusinessPhoneNumber`
      | `receiptPreset.displaySettings.businessDisplayOptions.showBusinessAddress`
      | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerEmail`
      | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerPhoneNumber`
      | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerShippingAddress`
      | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerBillingAddress`
      | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerVatId`
      | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerCompanyName`
      | `receiptPreset.displaySettings.itemsDisplayOptions.showItemsDescription`
      | `receiptPreset.displaySettings.itemsDisplayOptions.showTax`
      | `receiptPreset.displaySettings.totalsDisplayOptions.showTaxBreakdown`
      | `receiptPreset.displaySettings.totalsDisplayOptions.showItemSubtotalPerTaxBreakdown`
      | `receiptPreset.customFields.headerCustomFields`
      | `receiptPreset.customFields.headerCustomFields.${number}.value`
      | `receiptPreset.customFields.businessCustomFields`
      | `receiptPreset.customFields.customerCustomFields`
      | `receiptPreset.customFields.footerCustomFields`
      | `receiptPreset.customFields.footerCustomFields.${number}.title`
      | `receiptPreset.default`,
      6
    > & {
      __applicationErrorsType?: GetDefaultReceiptPresetApplicationErrors;
    }
  >;
}

export function updateReceiptPreset(
  httpClient: HttpClient
): UpdateReceiptPresetSignature {
  return (
    _id: string,
    receiptPreset: NonNullablePaths<UpdateReceiptPreset, `revision`, 2>
  ) =>
    universalUpdateReceiptPreset(
      _id,
      receiptPreset,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateReceiptPresetSignature {
  /**
   * Updates a receipt preset.
   *
   *
   * Each time the receipt preset is updated,
   * `revision` increments by 1.
   * The current `revision` must be passed when updating the receipt preset.
   * This ensures you're working with the latest receipt preset
   * and prevents unintended overwrites.
   * @param - Receipt preset ID.
   * @returns Updated receipt preset.
   */
  (
    _id: string,
    receiptPreset: NonNullablePaths<UpdateReceiptPreset, `revision`, 2>
  ): Promise<
    NonNullablePaths<
      ReceiptPreset,
      | `name`
      | `displaySettings.businessDisplayOptions.showBusinessEmail`
      | `displaySettings.businessDisplayOptions.showBusinessPhoneNumber`
      | `displaySettings.businessDisplayOptions.showBusinessAddress`
      | `displaySettings.customerDisplayOptions.showCustomerEmail`
      | `displaySettings.customerDisplayOptions.showCustomerPhoneNumber`
      | `displaySettings.customerDisplayOptions.showCustomerShippingAddress`
      | `displaySettings.customerDisplayOptions.showCustomerBillingAddress`
      | `displaySettings.customerDisplayOptions.showCustomerVatId`
      | `displaySettings.customerDisplayOptions.showCustomerCompanyName`
      | `displaySettings.itemsDisplayOptions.showItemsDescription`
      | `displaySettings.itemsDisplayOptions.showTax`
      | `displaySettings.totalsDisplayOptions.showTaxBreakdown`
      | `displaySettings.totalsDisplayOptions.showItemSubtotalPerTaxBreakdown`
      | `customFields.headerCustomFields`
      | `customFields.headerCustomFields.${number}.value`
      | `customFields.businessCustomFields`
      | `customFields.customerCustomFields`
      | `customFields.footerCustomFields`
      | `customFields.footerCustomFields.${number}.title`
      | `default`,
      5
    > & {
      __applicationErrorsType?: UpdateReceiptPresetApplicationErrors;
    }
  >;
}

export function setDefaultReceiptPreset(
  httpClient: HttpClient
): SetDefaultReceiptPresetSignature {
  return (receiptPresetId: string) =>
    universalSetDefaultReceiptPreset(
      receiptPresetId,
      // @ts-ignore
      { httpClient }
    );
}

interface SetDefaultReceiptPresetSignature {
  /**
   * Sets a receipt preset as default.
   *
   * The default preset is used during receipt creation when a preset isn't specified or doesn't exist.
   * @param - Receipt preset ID to set as default.
   */
  (receiptPresetId: string): Promise<
    NonNullablePaths<
      SetDefaultReceiptPresetResponse,
      | `receiptPreset.name`
      | `receiptPreset.displaySettings.businessDisplayOptions.showBusinessEmail`
      | `receiptPreset.displaySettings.businessDisplayOptions.showBusinessPhoneNumber`
      | `receiptPreset.displaySettings.businessDisplayOptions.showBusinessAddress`
      | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerEmail`
      | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerPhoneNumber`
      | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerShippingAddress`
      | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerBillingAddress`
      | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerVatId`
      | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerCompanyName`
      | `receiptPreset.displaySettings.itemsDisplayOptions.showItemsDescription`
      | `receiptPreset.displaySettings.itemsDisplayOptions.showTax`
      | `receiptPreset.displaySettings.totalsDisplayOptions.showTaxBreakdown`
      | `receiptPreset.displaySettings.totalsDisplayOptions.showItemSubtotalPerTaxBreakdown`
      | `receiptPreset.customFields.headerCustomFields`
      | `receiptPreset.customFields.headerCustomFields.${number}.value`
      | `receiptPreset.customFields.businessCustomFields`
      | `receiptPreset.customFields.customerCustomFields`
      | `receiptPreset.customFields.footerCustomFields`
      | `receiptPreset.customFields.footerCustomFields.${number}.title`
      | `receiptPreset.default`,
      6
    >
  >;
}

export function deleteReceiptPreset(
  httpClient: HttpClient
): DeleteReceiptPresetSignature {
  return (receiptPresetId: string) =>
    universalDeleteReceiptPreset(
      receiptPresetId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteReceiptPresetSignature {
  /**
   * Permanently deletes a receipt preset.
   * @param - Receipt preset ID to delete.
   */
  (receiptPresetId: string): Promise<
    void & {
      __applicationErrorsType?: DeleteReceiptPresetApplicationErrors;
    }
  >;
}

export function listReceiptPresets(
  httpClient: HttpClient
): ListReceiptPresetsSignature {
  return () =>
    universalListReceiptPresets(
      // @ts-ignore
      { httpClient }
    );
}

interface ListReceiptPresetsSignature {
  /**
   * Lists all available receipt presets.
   *
   * By default the returned receipt presets are sorted by `default` and `updatedDate` in descending order.
   */
  (): Promise<
    NonNullablePaths<
      ListReceiptPresetsResponse,
      | `receiptPresets`
      | `receiptPresets.${number}.name`
      | `receiptPresets.${number}.displaySettings.businessDisplayOptions.showBusinessEmail`
      | `receiptPresets.${number}.displaySettings.businessDisplayOptions.showBusinessPhoneNumber`
      | `receiptPresets.${number}.displaySettings.businessDisplayOptions.showBusinessAddress`
      | `receiptPresets.${number}.displaySettings.customerDisplayOptions.showCustomerEmail`
      | `receiptPresets.${number}.displaySettings.customerDisplayOptions.showCustomerPhoneNumber`
      | `receiptPresets.${number}.displaySettings.customerDisplayOptions.showCustomerShippingAddress`
      | `receiptPresets.${number}.displaySettings.customerDisplayOptions.showCustomerBillingAddress`
      | `receiptPresets.${number}.displaySettings.customerDisplayOptions.showCustomerVatId`
      | `receiptPresets.${number}.displaySettings.customerDisplayOptions.showCustomerCompanyName`
      | `receiptPresets.${number}.displaySettings.itemsDisplayOptions.showItemsDescription`
      | `receiptPresets.${number}.displaySettings.itemsDisplayOptions.showTax`
      | `receiptPresets.${number}.displaySettings.totalsDisplayOptions.showTaxBreakdown`
      | `receiptPresets.${number}.displaySettings.totalsDisplayOptions.showItemSubtotalPerTaxBreakdown`
      | `receiptPresets.${number}.default`,
      6
    > & {
      __applicationErrorsType?: ListReceiptPresetsApplicationErrors;
    }
  >;
}

export function updateExtendedFields(
  httpClient: HttpClient
): UpdateExtendedFieldsSignature {
  return (
    _id: string,
    namespace: string,
    options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
  ) =>
    universalUpdateExtendedFields(
      _id,
      namespace,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateExtendedFieldsSignature {
  /**
   * Updates the extended fields of a receipt preset, without incrementing revision.
   * @param - ID of the entity to update.
   * @param - Identifier for the app whose extended fields are being updated.
   * @param - Field options. The `namespaceData` object **must** be passed.
   */
  (
    _id: string,
    namespace: string,
    options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
  ): Promise<
    NonNullablePaths<
      UpdateExtendedFieldsResponse,
      | `receiptPreset.name`
      | `receiptPreset.displaySettings.businessDisplayOptions.showBusinessEmail`
      | `receiptPreset.displaySettings.businessDisplayOptions.showBusinessPhoneNumber`
      | `receiptPreset.displaySettings.businessDisplayOptions.showBusinessAddress`
      | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerEmail`
      | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerPhoneNumber`
      | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerShippingAddress`
      | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerBillingAddress`
      | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerVatId`
      | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerCompanyName`
      | `receiptPreset.displaySettings.itemsDisplayOptions.showItemsDescription`
      | `receiptPreset.displaySettings.itemsDisplayOptions.showTax`
      | `receiptPreset.displaySettings.totalsDisplayOptions.showTaxBreakdown`
      | `receiptPreset.displaySettings.totalsDisplayOptions.showItemSubtotalPerTaxBreakdown`
      | `receiptPreset.customFields.headerCustomFields`
      | `receiptPreset.customFields.headerCustomFields.${number}.value`
      | `receiptPreset.customFields.businessCustomFields`
      | `receiptPreset.customFields.customerCustomFields`
      | `receiptPreset.customFields.footerCustomFields`
      | `receiptPreset.customFields.footerCustomFields.${number}.title`
      | `receiptPreset.default`,
      6
    >
  >;
}

export const onReceiptPresetCreated = EventDefinition(
  'wix.receipts.presets.v1.receipt_preset_created',
  true,
  (event: ReceiptPresetCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<ReceiptPresetCreatedEnvelope>();
export const onReceiptPresetDeleted = EventDefinition(
  'wix.receipts.presets.v1.receipt_preset_deleted',
  true,
  (event: ReceiptPresetDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<ReceiptPresetDeletedEnvelope>();
export const onReceiptPresetUpdated = EventDefinition(
  'wix.receipts.presets.v1.receipt_preset_updated',
  true,
  (event: ReceiptPresetUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
          ],
        },
      ])
    )
)<ReceiptPresetUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  BaseEventMetadata,
  BusinessDisplayOptions,
  CreateReceiptPresetRequest,
  CreateReceiptPresetResponse,
  CustomField,
  CustomFields,
  CustomerDisplayOptions,
  DeleteReceiptPresetRequest,
  DeleteReceiptPresetResponse,
  DisplaySettings,
  DisplayValues,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  FooterCustomField,
  GetDefaultReceiptPresetRequest,
  GetDefaultReceiptPresetResponse,
  GetReceiptPresetRequest,
  GetReceiptPresetResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemsDisplayOption,
  ListReceiptPresetsRequest,
  ListReceiptPresetsResponse,
  MessageEnvelope,
  ReceiptPreset,
  ReceiptPresetCreatedEnvelope,
  ReceiptPresetDeletedEnvelope,
  ReceiptPresetUpdatedEnvelope,
  RestoreInfo,
  SetDefaultReceiptPresetRequest,
  SetDefaultReceiptPresetResponse,
  TotalsDisplayOption,
  UpdateExtendedFieldsOptions,
  UpdateExtendedFieldsRequest,
  UpdateExtendedFieldsResponse,
  UpdateReceiptPreset,
  UpdateReceiptPresetRequest,
  UpdateReceiptPresetResponse,
  WebhookIdentityType,
} from './receipts-presets-v1-receipt-preset-receipt-presets.universal.js';
